import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'

import { useContentApi } from '@/composables/useCmsContentApi'
import { useAppRouting } from '@/composables/useAppRouting'
import type { AnnualPlan, Subject } from '@/types/Subject'
import type { SubjectGuide } from '@/types/SubjectGuide'
import type { Topic } from '@/types/Topic'
import RouteParam from '@/enums/RouteParam'

export const useSubject = () => {
  const { getCurrentSubject, getSubjectGuideChild, getTopicsAndCredits, getAnnualPlan } = useContentApi()
  const { getContentIdFromRouteParam } = useAppRouting()

  const subjectContentId = getContentIdFromRouteParam(RouteParam.Subject)

  const { data: subject } = useQuery<Subject>({
    queryKey: ['currentSubject', subjectContentId],
    queryFn: () => getCurrentSubject(subjectContentId),
    staleTime: Infinity
  })

  const { data: subjectGuide } = useQuery<SubjectGuide>({
    queryKey: ['subjectGuide', subjectContentId],
    queryFn: () => getSubjectGuideChild(subjectContentId),
    staleTime: Infinity,
    enabled: !!subjectContentId
  })

  const { data: annualPlan } = useQuery<AnnualPlan>({
    queryKey: ['annualPlan', subjectContentId],
    queryFn: () => getAnnualPlan(subject.value?.annualPlanId as string),
    staleTime: Infinity,
    enabled: computed(() => !!subject.value?.annualPlanId)
  })

  const { data: topicsAndCredits, isLoading: isTopicsAndCreditsLoading } = useQuery({
    queryKey: ['topic', subjectContentId],
    queryFn: async () => await getTopicsAndCredits(subjectContentId),
    staleTime: Infinity,
  })

  const topicList = computed(() => (topicsAndCredits?.value?.topics || []).filter((topic: Topic) => !topic.isComingSoon))
  const comingSoonTopicList = computed(() => (topicsAndCredits?.value?.topics || []).filter((topic: Topic) => topic.isComingSoon))
  const credits = computed(() => topicsAndCredits.value?.credits)

  return {
    comingSoonTopicList,
    credits,
    isTopicsAndCreditsLoading,
    subject,
    subjectGuide,
    topicList,
    annualPlan
  }
}